import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { fetchData, deleteData, activateData } from '../../apiService';
import { useAuth } from '../../AuthContext';

const CategorySalesChart = () => {
  const [data, setData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, companyId, branchId, userId, permissions, token, logout, branchstate } = useAuth();

  useEffect(() => {
    const fetchSalesData = async () => {

      // Get current date
      const toDateObj = new Date();
      const toDate = toDateObj.toISOString().split('T')[0]; // Format: YYYY-MM-DD

      // Get date of same day previous month
      const fromDateObj = new Date(toDateObj);
      fromDateObj.setMonth(fromDateObj.getMonth() - 1);
      const fromDate = fromDateObj.toISOString().split('T')[0];

      try {
        const response = await fetchData('ReportApi', `/SalesCategoryWise/${fromDate}/${toDate}/${companyId}`, token);
        if (response.responseCode === '1111') {
          //setDataItem(response.response_data);
          const top10 = [...response.response_data]
            .sort((a, b) => b.SaleQuantity - a.SaleQuantity)
            .slice(0, 10);
          setData(top10);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching sales data:', error);
      }
    };


    const fetchSalesPaymentStatus = async () => {
      const toDateObj = new Date();
      const toDate = toDateObj.toISOString().split('T')[0];

      const fromDateObj = new Date(toDateObj);
      fromDateObj.setMonth(fromDateObj.getMonth() - 1);
      const fromDate = fromDateObj.toISOString().split('T')[0];

      try {
        const response = await fetchData(
          'ReportApi',
          `/SalesPaymentStatus/${fromDate}/${toDate}/${companyId}`,
          token
        );

        if (response.responseCode === '1111') {
          const data = response.response_data;

          if (data.length > 0) {
            const { SaleAmount, PaidAmount } = data[0]; // Assuming only one object
            const unpaid = SaleAmount - PaidAmount;

            const chartData = [
              { name: 'Paid', value: PaidAmount },
              { name: 'Unpaid', value: unpaid }
            ];

            setSummaryData(chartData);
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Error fetching sales data:', error);
      }
    };


    fetchSalesData();
    fetchSalesPaymentStatus();
  }, []);

  return (
    <div
      className="max-w-6xl mx-auto px-4"
      style={{
        fontFamily: 'monospace',
        color: '#1A1A1D',
        marginTop: '5px',
      }}
    >
      {loading ? (
        <div className="flex justify-center items-center h-48 text-gray-500">
          <Loader2 className="animate-spin h-8 w-8 mr-2 text-blue-500" />
          Loading chart...
        </div>
      ) : (
        <div className="flex flex-col md:flex-row gap-4">
          {/* Bar Chart Card */}
          <div className="w-full md:w-2/3 bg-white rounded-xl shadow p-4">
            <h5
              className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2"
              style={{
                fontSize: '14px',
                fontFamily: 'monospace',
                color: '#1A1A1D',
                textTransform: 'uppercase',
              }}
            >
              📊 Category-wise Sale Quantity (Top 10 in Last Month)
            </h5>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart
                data={data}
                margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
                style={{ fontFamily: 'monospace', color: '#1A1A1D' }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="CategoryName"
                  angle={-15}
                  textAnchor="end"
                  interval={0}
                  height={40}
                  tick={{ fontSize: 10 }}
                  tickMargin={8}

                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#60a5fa" stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <Bar
                  dataKey="SaleQuantity"
                  name="Sale Quantity"
                  radius={[10, 10, 0, 0]}
                  barSize={40}
                >
                  {/* {data.map((entry, index) => (
                    <Cell
                      key={`bar-cell-${index}`}
                      fill={[
                        '#93c5fd',
                        '#a7f3d0',
                        '#fcd34d',
                        '#fca5a5',
                        '#d8b4fe',
                        '#f9a8d4',
                        '#fdba74',
                        '#6ee7b7',
                        '#bfdbfe',
                        '#fde68a'
                      ][index % 10]}
                    />
                  ))} */}

                  {data.map((entry, index) => (
                    <Cell
                      key={`bar-cell-${index}`}
                      fill={[
                        '#dbeafe', // blue-100
                        '#bfdbfe', // blue-200
                        '#93c5fd', // blue-300
                        '#60a5fa', // blue-400
                        '#3b82f6', // blue-500
                        '#2563eb', // blue-600
                        '#1d4ed8', // blue-700
                        '#1e40af', // blue-800
                        '#1e3a8a', // blue-900
                        '#172554'  // blue-950 (very dark blue)
                      ][index % 10]}
                    />
                  ))}

                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="w-full md:w-1/3 bg-white rounded-xl shadow p-4">
            <h6
              className="text-sm font-semibold mb-2 text-center text-gray-700"
              style={{
                fontFamily: 'monospace',
                color: '#1A1A1D',
                textTransform: 'uppercase'
              }}
            >
              Sales Payment Status(Last 1 Month)
            </h6>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Tooltip />
                <Pie
                  data={summaryData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  style={{ fontSize: '9px', fontWeight: 'bold' }}
                  outerRadius={80}
                  label={({ name, percent }) =>
                    `${name} (${(percent * 100).toFixed(0)}%)`
                  }
                >
                  <Cell fill="#4ade80" /> {/* Green - Paid */}
                  <Cell fill="#f87171" /> {/* Red - Unpaid */}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>

        </div>
      )
      }
    </div >
  );

};

export default CategorySalesChart;
